'use client';

import { resolveCookieDomain } from '../config/get-config';

export const AUTH_TOKEN_STORAGE_KEY = 'user_token';
export const REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';
export const AUTH_STATE_STORAGE_KEY = 'userAuthState';
export const USER_EMAIL_STORAGE_KEY = 'user_email';
export const AUTH_CHANGED_EVENT = 'pteultra-auth-changed';

const GOOGLE_AUTO_SIGN_IN_SUPPRESSED_KEY = 'google_auto_sign_in_suppressed';

// Auth cookies expire in 30 days; cleared explicitly on sign-out.
const COOKIE_MAX_AGE = 30 * 24 * 60 * 60;

type PersistAuthenticatedUserSessionOptions = {
    accessToken?: string | null;
    refreshToken?: string | null;
    email?: string | null;
};

type ClearStoredAuthTokensOptions = {
    suppressGoogleAutoSignIn?: boolean;
};

type GoogleIdentityClient = {
    cancel?: () => void;
    disableAutoSelect?: () => void;
    revoke?: (email: string, done: () => void) => void;
};

function getGoogleIdentity() {
    if (typeof window === 'undefined') {
        return null;
    }

    return (window as Window & {
        google?: {
            accounts?: {
                id?: GoogleIdentityClient;
            };
        };
    }).google?.accounts?.id ?? null;
}

function dispatchAuthChanged() {
    if (typeof window === 'undefined') {
        return;
    }

    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

function getLegacyApexCookieDomain(): string {
    if (typeof window === 'undefined') {
        return '';
    }

    const parts = window.location.hostname.split('.').filter(Boolean);
    if (parts.length < 2) {
        return '';
    }

    return `.${parts.slice(-2).join('.')}`;
}

function getCookieDomain(): string {
    if (typeof window === 'undefined') {
        return '';
    }

    return resolveCookieDomain(window.__APP_CONFIG__ ?? null, window.location.hostname);
}

function buildCookieAttributes(remove = false, domain = ''): string {
    const domainAttr = domain ? `; domain=${domain}` : '';
    const secureAttr = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; secure' : '';
    const maxAgeAttr = remove ? '; max-age=0' : `; max-age=${COOKIE_MAX_AGE}`;
    return `; path=/${domainAttr}${maxAgeAttr}${secureAttr}; samesite=lax`;
}

function getCookieDomainsToClear(): string[] {
    const configuredDomain = getCookieDomain();
    const legacyApexDomain = getLegacyApexCookieDomain();

    return Array.from(new Set(['', configuredDomain, legacyApexDomain].filter(Boolean)));
}

export function getCookieValue(name: string): string {
    if (typeof document === 'undefined') return '';
    const escaped = name.replace(/[.+*?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
}

function setCookieValue(name: string, value: string | null | undefined): void {
    if (typeof document === 'undefined') return;
    const normalizedValue = typeof value === 'string' ? value.trim() : '';
    if (normalizedValue) {
        removeCookieValue(name);
        document.cookie = `${name}=${encodeURIComponent(normalizedValue)}${buildCookieAttributes(false, getCookieDomain())}`;
    } else {
        removeCookieValue(name);
    }
}

function removeCookieValue(name: string): void {
    if (typeof document === 'undefined') return;

    getCookieDomainsToClear().forEach((domain) => {
        document.cookie = `${name}=${buildCookieAttributes(true, domain)}`;
    });
}

export function hasStoredAuthToken() {
    if (typeof window === 'undefined') {
        return false;
    }

    return Boolean(getCookieValue(AUTH_TOKEN_STORAGE_KEY));
}

export function isGoogleAutoSignInSuppressed() {
    if (typeof window === 'undefined') {
        return false;
    }

    return localStorage.getItem(GOOGLE_AUTO_SIGN_IN_SUPPRESSED_KEY) === '1';
}

export function clearGoogleAutoSignInSuppression() {
    if (typeof window === 'undefined') {
        return;
    }

    localStorage.removeItem(GOOGLE_AUTO_SIGN_IN_SUPPRESSED_KEY);
}

export function persistAuthenticatedUserSession({ accessToken, refreshToken, email }: PersistAuthenticatedUserSessionOptions) {
    if (typeof document === 'undefined') {
        return;
    }

    setCookieValue(AUTH_TOKEN_STORAGE_KEY, accessToken);
    setCookieValue(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
    setCookieValue(USER_EMAIL_STORAGE_KEY, email);

    const normalizedAccessToken = typeof accessToken === 'string' ? accessToken.trim() : '';
    if (normalizedAccessToken) {
        setCookieValue(AUTH_STATE_STORAGE_KEY, JSON.stringify({ authToken: normalizedAccessToken }));
    } else {
        removeCookieValue(AUTH_STATE_STORAGE_KEY);
    }

    clearGoogleAutoSignInSuppression();
    dispatchAuthChanged();
}

export function clearStoredAuthTokens(options: ClearStoredAuthTokensOptions = { suppressGoogleAutoSignIn: false }) {
    if (typeof window === 'undefined') {
        return;
    }

    const googleIdentity = getGoogleIdentity();
    const hadAccessToken = Boolean(getCookieValue(AUTH_TOKEN_STORAGE_KEY));
    const hadRefreshToken = Boolean(getCookieValue(REFRESH_TOKEN_STORAGE_KEY));
    const hadSerializedAuthState = Boolean(getCookieValue(AUTH_STATE_STORAGE_KEY));
    const storedEmail = getCookieValue(USER_EMAIL_STORAGE_KEY);
    const hadStoredEmail = Boolean(storedEmail);
    const shouldSuppressGoogleAutoSignIn = options.suppressGoogleAutoSignIn ?? false;
    const hadSuppressedGoogleAutoSignIn = isGoogleAutoSignInSuppressed();

    removeCookieValue(AUTH_TOKEN_STORAGE_KEY);
    removeCookieValue(REFRESH_TOKEN_STORAGE_KEY);
    removeCookieValue(AUTH_STATE_STORAGE_KEY);
    removeCookieValue(USER_EMAIL_STORAGE_KEY);

    if (shouldSuppressGoogleAutoSignIn) {
        localStorage.setItem(GOOGLE_AUTO_SIGN_IN_SUPPRESSED_KEY, '1');
    } else {
        clearGoogleAutoSignInSuppression();
    }

    const authStateChanged = hadAccessToken || hadRefreshToken || hadSerializedAuthState || hadStoredEmail;
    const googleSuppressionChanged = shouldSuppressGoogleAutoSignIn
        ? !hadSuppressedGoogleAutoSignIn
        : hadSuppressedGoogleAutoSignIn;

    try {
        googleIdentity?.cancel?.();
        googleIdentity?.disableAutoSelect?.();
        if (storedEmail && typeof googleIdentity?.revoke === 'function') {
            googleIdentity.revoke(storedEmail, () => undefined);
        }
    } catch {
        // Ignore Google SDK cleanup failures and continue clearing local auth state.
    }

    if (authStateChanged || googleSuppressionChanged) {
        dispatchAuthChanged();
    }
}